/**
 * Utility functions for data type conversions and validations
 */

/**
 * Safely converts a value to an integer for API calls
 * @param value - The value to convert (string, number, or undefined)
 * @returns The integer value or throws an error if conversion fails
 */
export const toApiInt = (value: string | number | undefined): number => {
  if (value === undefined || value === null) {
    throw new Error('Value is required for API call')
  }
  
  const num = typeof value === 'string' ? parseInt(value, 10) : value
  
  if (isNaN(num)) {
    throw new Error(`Invalid integer value: ${value}`)
  }
  
  return num
}

/**
 * Safely converts a value to an integer for API calls with fallback
 * @param value - The value to convert (string, number, or undefined)
 * @param fallback - Fallback value if conversion fails
 * @returns The integer value or fallback
 */
export const toApiIntSafe = (value: string | number | undefined, fallback: number = 0): number => {
  if (value === undefined || value === null) {
    return fallback
  }
  
  const num = typeof value === 'string' ? parseInt(value, 10) : value
  
  if (isNaN(num)) {
    return fallback
  }
  
  return num
}

/**
 * Validates that a value is a valid integer for API calls
 * @param value - The value to validate
 * @returns True if the value is a valid integer
 */
export const isValidApiInt = (value: string | number | undefined): boolean => {
  if (value === undefined || value === null) {
    return false
  }
  
  const num = typeof value === 'string' ? parseInt(value, 10) : value
  
  return !isNaN(num)
}
